import { useMemo, useState, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDateSort } from '../../../../hooks/useDateSort';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import { ProfitLossDailyRow } from '../../../../types/pointhomeProfitLoss';
import type { Dayjs } from 'dayjs';

type Props = {
  rows: ProfitLossDailyRow[];
  countLabel: string;
  cashLabel: string;
  cashTooltip: string;
  sheetName: string;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

const formatCurrency = (value: number) => `₩${Math.round(value).toLocaleString('ko-KR')}`;
const formatNumber = (value: number) => Math.round(value).toLocaleString('ko-KR');
// 엑셀용: 통화기호 없이 숫자만
const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');

export default function PointhomeProfitLossContentTable({
  rows,
  countLabel,
  cashLabel,
  cashTooltip,
  sheetName,
  selectedApp,
  startDate,
  endDate,
}: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);

  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(rows);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page]
  );

  // 합계 계산 (손익률 제외 합계, 손익률은 순수익 합계/총 매출 합계*100)
  const totals = useMemo(() => {
    const totalRevenue = sortedData.reduce((sum, item) => sum + item.revenue, 0);
    const totalCash = sortedData.reduce((sum, item) => sum + Math.round(item.cash / 6), 0);
    const totalProfit = totalRevenue - totalCash;
    const totalCount = sortedData.reduce((sum, item) => sum + item.count, 0);

    // 손익률 = (순수익 합계 / 총 매출 합계) * 100
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalProfit,
      totalCount,
      totalCash,
      profitMargin,
    };
  }, [sortedData]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  // 손익률은 증감 부호와 함께 문자열로 전달
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((item) => {
      const dailyCash = Math.round(item.cash / 6);
      const dailyProfit = item.revenue - dailyCash;
      const dailyProfitMargin = item.revenue > 0 ? (dailyProfit / item.revenue) * 100 : 0;
      const profitMarginText = `${dailyProfitMargin >= 0 ? '+' : ''}${dailyProfitMargin.toFixed(0)}%`;
      return [
        item.date,
        formatNumberOnly(item.revenue),
        formatNumberOnly(dailyProfit),
        formatNumber(item.count),
        formatNumberOnly(dailyCash),
        profitMarginText,
      ];
    });
  }, [sortedData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu
          tableRef={tableRef}
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName={sheetName}
        />
      </Box>
      {/* 테이블 */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 2,
        }}
      >
        <TableContainer sx={{ width: '100%' }}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                    날짜
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">총 매출</TableCell>
                <TableCell align="right">순수익</TableCell>
                <TableCell align="right">{countLabel}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {cashLabel}
                    <Tooltip title={cashTooltip} arrow>
                      <IconButton
                        size="small"
                        sx={{
                          p: 0,
                          width: 18,
                          height: 18,
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        <HelpOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right">손익률</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* 합계 행 */}
              <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totals.totalRevenue)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totals.totalProfit)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(totals.totalCount)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totals.totalCash)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {totals.profitMargin >= 0 ? '+' : ''}
                    {totals.profitMargin.toFixed(0)}%
                    <Tooltip title="조회한 기간의 순수익 합계/총 매출 합계를 백분율로 나타낸 값입니다." arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
              {paged.map((item) => {
                const dailyCash = Math.round(item.cash / 6);
                const dailyProfit = item.revenue - dailyCash;
                // 손익률 = (총매출 - 캐시/6) / 총매출 * 100
                const dailyProfitMargin = item.revenue > 0 ? (dailyProfit / item.revenue) * 100 : 0;
                return (
                  <TableRow key={item.date}>
                    <TableCell>{item.date}</TableCell>
                    <TableCell align="right">{formatCurrency(item.revenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(dailyProfit)}</TableCell>
                    <TableCell align="right">{formatNumber(item.count)}</TableCell>
                    <TableCell align="right">{formatCurrency(dailyCash)}</TableCell>
                    <TableCell align="right">
                      {dailyProfitMargin >= 0 ? '+' : ''}
                      {dailyProfitMargin.toFixed(0)}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sortedData.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
          />
        </TableContainer>
      </Box>
    </Box>
  );
}
